import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { ArrowLeft, Clock, Coins, GraduationCap, Mail, ThumbsUp } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePartnerStudents } from "@/hooks/usePartnerData";
import { useCreateTip } from "@/hooks/useTips";
import { PageSkeleton } from "@/components/shared/Skeletons";
import TipPaymentModal from "@/components/shared/TipPaymentModal";

export const Route = createFileRoute("/partner/students/$id")({ component: PartnerStudentDetail });

function InfoRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-muted-foreground [&_svg]:size-4" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-caption text-muted-foreground">{label}</dt>
        <dd className="text-sm break-words text-foreground">{children}</dd>
      </div>
    </div>
  );
}

function PartnerStudentDetail() {
  const { id } = Route.useParams();
  const { data: students = [], isLoading } = usePartnerStudents();
  const createTip = useCreateTip();
  const [tipModalOpen, setTipModalOpen] = React.useState(false);

  const student = students?.find?.((x: any) => String(x.id) === String(id));

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!student) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Student not found"
          description="This student may no longer be assigned to your placement slots."
        />
        <div className="flex justify-center">
          <Button variant="outline" asChild>
            <Link to="/partner/students">Back to students</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title={student.name}
          subtitle={student.course}
          breadcrumbs={[{ label: "Students", path: "/partner/students" }, { label: student.name }]}
          action={
            <Button variant="outline" asChild>
              <Link to="/partner/students">
                <ArrowLeft />
                Back to students
              </Link>
            </Button>
          }
        />

        <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <section className="rounded-md border border-border bg-card p-5 md:sticky md:top-6">
            <div className="flex flex-col items-center text-center">
              <Avatar className="mb-4 size-16">
                <AvatarFallback className="text-xl">{student.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <h2 className="text-title text-foreground">{student.name}</h2>
              <p className="mt-0.5 text-sm break-all text-muted-foreground">{student.email}</p>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <Button asChild className="w-full">
                <Link to="/partner/recommendations/new">
                  <ThumbsUp />
                  Submit recommendation
                </Link>
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setTipModalOpen(true)}>
                <Coins />
                Tip student
              </Button>
            </div>

            <dl className="mt-5 space-y-4 border-t border-border pt-5">
              <InfoRow icon={<GraduationCap />} label="University">
                {student.university}
              </InfoRow>
              <InfoRow icon={<Clock />} label="Hours logged">
                <span className="font-mono tabular-nums">{student.hoursLogged}h</span>
              </InfoRow>
              <InfoRow icon={<Mail />} label="Email">
                <a href={`mailto:${student.email}`} className="hover:underline">
                  {student.email}
                </a>
              </InfoRow>
            </dl>
          </section>

          <SectionCard title="Student information">
            <dl className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-sm border border-border bg-muted/40 p-3">
                <dt className="eyebrow mb-1">Course</dt>
                <dd className="text-sm font-medium text-foreground">{student.course}</dd>
              </div>
              <div className="rounded-sm border border-border bg-muted/40 p-3">
                <dt className="eyebrow mb-1">Year of study</dt>
                <dd className="text-sm font-medium text-foreground">Year {student.year}</dd>
              </div>
              <div className="rounded-sm border border-border bg-muted/40 p-3">
                <dt className="eyebrow mb-1">Registered date</dt>
                <dd className="text-sm font-medium text-foreground tabular-nums">
                  {student.registeredDate
                    ? new Date(student.registeredDate).toLocaleDateString("en-GB")
                    : "N/A"}
                </dd>
              </div>
              <div className="rounded-sm border border-border bg-muted/40 p-3">
                <dt className="eyebrow mb-1">Status</dt>
                <dd>
                  <StatusChip status={student.status} />
                </dd>
              </div>
            </dl>

            <h3 className="mt-6 mb-2 text-sm font-medium text-foreground">Skills</h3>
            <div className="flex flex-wrap gap-1.5">
              {(student.skills || []).length > 0 ? (
                (student.skills || []).map((skill: string) => (
                  <Badge key={skill} variant="secondary">
                    {skill}
                  </Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No skills listed.</p>
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      <TipPaymentModal
        open={tipModalOpen}
        onClose={() => setTipModalOpen(false)}
        onSubmit={({ phone, network, amount, message }) => {
          createTip.mutate(
            { studentId: Number(id), amount, message },
            { onSuccess: () => setTipModalOpen(false) },
          );
        }}
        studentName={student?.name || "Student"}
        isPending={createTip.isPending}
      />
    </>
  );
}
